import {ApplicationRef, ComponentFactoryResolver, ComponentRef, Injectable, Injector} from '@angular/core'
import {DialogComponent} from './dialog.component'
import {take} from 'rxjs/operators'
import {Monologic} from './monologic.interface'
import {Dialogistic} from './dialogistic.interface'
import {DocumentService} from '../utils/document.service'
import {Observable} from 'rxjs'

export interface DialogContext<Output, Component> {
  dialog: DialogComponent<Output>
  content: Component
}

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  constructor(
    private readonly componentFactoryResolver: ComponentFactoryResolver,
    private readonly applicationRef: ApplicationRef,
    private readonly injector: Injector,
    private readonly documentService: DocumentService,
  ) {}

  show<Component extends Dialogistic<unknown, unknown> | Monologic<unknown>>(
    contentType: new (...params) => Component,
    data?: DataOf<Component>,
    config?: {
      title?: string
      size?: 'sm' | 'md' | 'lg'
    },
  ): DialogContext<OutputOf<Component>, Component> {
    return this.appendToBody(contentType, data, config)
  }

  private appendToBody<Component extends Dialogistic<Data, Output> | Monologic<Data>, Data, Output>(
    contentType: new (...params) => Component,
    data?: Data,
    config?: {
      title?: string
      size?: 'sm' | 'md' | 'lg'
    },
  ): DialogContext<Output, Component> {
    // TODO: migrate to ViewContainerRef.createComponent
    const dialogFactory =
      this.componentFactoryResolver.resolveComponentFactory<DialogComponent<Output>>(DialogComponent)
    const contentCompRef = this.componentFactoryResolver.resolveComponentFactory(contentType).create(this.injector)

    this.applicationRef.attachView(contentCompRef.hostView)
    const dialogCompRef = dialogFactory.create(this.injector, [[contentCompRef.location.nativeElement]])
    dialogCompRef.instance.title = config?.title
    dialogCompRef.instance.size = config?.size ?? 'md'
    dialogCompRef.instance.closed.pipe(take(1)).subscribe(() => {
      this.destroyComponent(contentCompRef)
      this.destroyComponent(dialogCompRef)
    })

    const containsKey = <T>(key: keyof T) => key in contentCompRef.instance
    if (containsKey<Dialogistic<Data, Output>>('reply$')) {
      const dialog = contentCompRef.instance as unknown as Dialogistic<Data, Output>

      dialog
        .reply$()
        .pipe(take(1))
        .subscribe(output => {
          dialogCompRef.instance.close(output)
        })
    }

    if (containsKey<Monologic<Data>>('setData') && data) {
      const confirmable = contentCompRef.instance as unknown as Monologic<Data>
      confirmable.setData(data)
    }

    this.applicationRef.attachView(dialogCompRef.hostView)
    const element = dialogCompRef.location.nativeElement
    this.documentService.appendChildToBody(element)

    return {dialog: dialogCompRef.instance, content: contentCompRef.instance}
  }

  private destroyComponent<T>(componentRef: ComponentRef<T>): void {
    this.applicationRef.detachView(componentRef.hostView)
    componentRef.destroy()
  }
}

// types that ensure type inference for Data from a component implementing Dialogistic or Monoligic
// based on the type of the first parameter of setData (which must be Data !)
type DataOf<Component extends Dialogistic<unknown, unknown> | Monologic<unknown>> = Parameters<Component['setData']>[0]

// types that ensure type inference for Output from a component implementing Dialogistic or Monoligic
// based on the return type of reply$ (which must be Observable<Output> !)
// when the component implements Monologic, return type is never
type OutputOf<Component extends Dialogistic<unknown, unknown> | Monologic<unknown>> = Component extends Dialogistic<
  unknown,
  unknown
>
  ? ReturnType<Component['reply$']> extends Observable<infer Output>
    ? Output
    : never
  : never
